/**
 * Display helpers only. All date arithmetic (working days, due dates, reminders) is done by the
 * server — the UI never re-implements business rules.
 */
let zone = 'Asia/Kolkata';
export function setZone(tz: string) {
  zone = tz;
}
export function getZone() {
  return zone;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function parseISODate(d: string): { y: number; m: number; day: number; dow: number } {
  const [y, m, day] = d.split('-').map(Number) as [number, number, number];
  const dow = new Date(Date.UTC(y, m - 1, day)).getUTCDay();
  return { y, m, day, dow };
}

/** Today's date (YYYY-MM-DD) in the business timezone. */
export function todayISO(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}

export function daysBetween(a: string, b: string): number {
  const pa = parseISODate(a);
  const pb = parseISODate(b);
  return Math.round((Date.UTC(pb.y, pb.m - 1, pb.day) - Date.UTC(pa.y, pa.m - 1, pa.day)) / 86_400_000);
}

/** "Mon, 28 Sep" (+ year when not the current year). */
export function formatDate(d: string | null | undefined, withYear?: boolean): string {
  if (!d) return '—';
  const p = parseISODate(d);
  const showYear = withYear ?? p.y !== Number(todayISO().slice(0, 4));
  return `${DAYS[p.dow]}, ${p.day} ${MONTHS[p.m - 1]}${showYear ? ` ${p.y}` : ''}`;
}

export function formatShortDate(d: string | null | undefined): string {
  if (!d) return '—';
  const p = parseISODate(d);
  return `${p.day} ${MONTHS[p.m - 1]}`;
}

/** Today / Tomorrow / Yesterday / In 3 days / 2 days ago — else the date. */
export function relativeDay(d: string | null | undefined): string {
  if (!d) return 'No date';
  const diff = daysBetween(todayISO(), d);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  if (diff > 1 && diff <= 6) return `${formatDate(d)}`;
  return formatDate(d);
}

export function formatDue(t: { dueDate: string | null; dueTime: string | null }): string {
  if (!t.dueDate) return 'No date';
  return `${relativeDay(t.dueDate)}${t.dueTime ? ` · ${formatTime(t.dueTime)}` : ''}`;
}

export function formatTime(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number) as [number, number];
  const ap = h >= 12 ? 'PM' : 'AM';
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${String(m).padStart(2, '0')} ${ap}`;
}

/** Wall-clock parts of an instant in the business timezone. */
function zonedParts(iso: string): { date: string; time: string; seconds: string } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(iso));
  const get = (type: string) => parts.find((p) => p.type === type)!.value;
  return { date: `${get('year')}-${get('month')}-${get('day')}`, time: `${get('hour')}:${get('minute')}`, seconds: get('second') };
}

/** "Sun, 27 Sep 2026 · 8:22 PM" — same style as due dates everywhere else in the app. */
export function formatDateTime(iso: string | null | undefined, opts: { seconds?: boolean } = {}): string {
  if (!iso) return '—';
  const p = zonedParts(iso);
  const time = formatTime(p.time);
  return `${formatDate(p.date, true)} · ${opts.seconds ? time.replace(' ', `:${p.seconds} `) : time}`;
}

export function formatTimeOf(iso: string): string {
  return formatTime(zonedParts(iso).time);
}

export function formatRelativeTime(iso: string): string {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 45) return 'just now';
  if (diff < 3600) return `${Math.round(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)} h ago`;
  return formatDateTime(iso);
}

export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function money(amount: string | null, currency = 'INR'): string {
  if (!amount) return '—';
  const n = Number(amount);
  if (!Number.isFinite(n)) return amount;
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: n % 1 === 0 ? 0 : 2 }).format(n);
}

/** Converts a local date + time (in the business timezone) into an ISO instant — used for custom snooze. */
export function zonedToISO(date: string, time: string): string {
  // Find the UTC instant whose wall-clock in `zone` equals date+time (two-pass offset correction).
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const [hh, mm] = time.split(':').map(Number) as [number, number];
  const guess = Date.UTC(y, m - 1, d, hh, mm);
  const offset = (t: number) => {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: zone, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' }).formatToParts(new Date(t));
    const get = (type: string) => Number(parts.find((p) => p.type === type)!.value);
    return Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute')) - t;
  };
  let t = guess - offset(guess);
  t = guess - offset(t);
  return new Date(t).toISOString();
}
